import { resolveClient } from './supabase';
import type { SubmissionThread } from '@lingaai/types';
import type { SupabaseClient } from '@supabase/supabase-js';

// File type that works in both web and React Native
export type FileInput = File | { uri: string; name: string; type: string };

export async function uploadFile(submissionId: string, file: FileInput, client?: SupabaseClient): Promise<SubmissionThread> {
  const supabase = resolveClient(client);

  // 1. Fetch submission to get tenant_id and portfolio_company_id
  const { data: submission, error: submissionError } = await supabase.from('submissions').select('tenant_id, portfolio_company_id').eq('id', submissionId).single();

  if (submissionError) throw new Error(`Failed to fetch submission: ${submissionError.message}`);
  if (!submission) throw new Error(`Submission not found: ${submissionId}`);

  // 2. Fetch tenant to get workspace_slug
  const { data: tenant, error: tenantError } = await supabase.from('tenants').select('workspace_slug').eq('id', submission.tenant_id).single();

  if (tenantError) throw new Error(`Failed to fetch tenant: ${tenantError.message}`);
  if (!tenant) throw new Error(`Tenant not found: ${submission.tenant_id}`);

  // 3. Create storage path: {workspace_slug}/{portfolio_company_id}/{submission_id}/{filename}
  const storagePath = `${tenant.workspace_slug}/${submission.portfolio_company_id}/${submissionId}/${file.name}`;

  // 4. Handle both web File and React Native file object
  const fileData =
    'uri' in file
      ? await fetch(file.uri).then((r) => r.blob()) // React Native
      : file; // Web

  // 5. Upload to Supabase Storage
  const { error: uploadError } = await supabase.storage.from('documents').upload(storagePath, fileData);

  if (uploadError) throw uploadError;

  // 6. Store file metadata in database
  const { data, error } = await supabase
    .from('submission_thread')
    .insert({
      tenant_id: submission.tenant_id,
      submission_id: submissionId,
      file_name: file.name,
      file_path: storagePath,
      file_size: 'size' in file ? file.size : 0,
      mime_type: file.type,
      source_type: 'upload', // Manually uploaded file
    })
    .select()
    .single();

  if (error) {
    // Clean up storage if database insert fails
    await supabase.storage.from('documents').remove([storagePath]);
    throw error;
  }

  return data as SubmissionThread;
}

export async function getFiles(submissionId: string, client?: SupabaseClient): Promise<SubmissionThread[]> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase.from('submission_thread').select('*').eq('submission_id', submissionId);

  if (error) throw error;
  return data as SubmissionThread[];
}

/**
 * Get a single file by ID with extracted text.
 * Used by agents to read file contents.
 */
export async function getFileById(fileId: string, client?: SupabaseClient): Promise<SubmissionThread> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase.from('submission_thread').select('*').eq('id', fileId).single();

  if (error) throw error;
  return data as SubmissionThread;
}

export async function downloadFile(filePath: string, client?: SupabaseClient): Promise<Blob> {
  const supabase = resolveClient(client);

  // First, check if file exists to provide better error messages
  const { data: existsData, error: existsError } = await supabase.storage.from('documents').list(filePath.substring(0, filePath.lastIndexOf('/')), {
    search: filePath.substring(filePath.lastIndexOf('/') + 1),
  });

  if (existsError) {
    console.error('Error checking file existence:', existsError);
  } else if (!existsData || existsData.length === 0) {
    throw new Error(`File not found in storage: ${filePath}. The file may not have been uploaded or was deleted.`);
  }

  // Download the file
  const { data, error } = await supabase.storage.from('documents').download(filePath);

  if (error) {
    // Enhance error with more context
    const enhancedError = new Error(`Failed to download file from storage: ${filePath}. ` + `Error: ${error.message || JSON.stringify(error)}`);
    (enhancedError as any).originalError = error;
    throw enhancedError;
  }

  return data;
}

export async function getFileUrl(filePath: string, client?: SupabaseClient): Promise<string> {
  const supabase = resolveClient(client);

  const { data } = supabase.storage.from('documents').getPublicUrl(filePath);

  return data.publicUrl;
}

export async function deleteFile(fileId: string, client?: SupabaseClient): Promise<void> {
  const supabase = resolveClient(client);

  // Get file path before deleting metadata
  const { data: fileData, error: fetchError } = await supabase.from('submission_thread').select('file_path').eq('id', fileId).single();

  if (fetchError) throw fetchError;

  // Delete from storage (only if file_path exists - email bodies don't have storage files)
  if (fileData.file_path) {
    const { error: storageError } = await supabase.storage.from('documents').remove([fileData.file_path]);

    if (storageError) throw storageError;
  }

  // Delete metadata
  const { error: dbError } = await supabase.from('submission_thread').delete().eq('id', fileId);

  if (dbError) throw dbError;
}

/**
 * Get file statistics (count and total size), optionally filtered by portfolio company.
 */
export async function getFilesStats(portfolioCompanyId?: string, client?: SupabaseClient): Promise<{ count: number; totalBytes: number }> {
  const supabase = resolveClient(client);

  let query = supabase.from('submission_thread').select('file_size, submission_id');

  const { data, error } = await query;
  if (error) throw error;

  // If filtering by portfolio company, need to check submission's portfolio_company_id
  let filteredData = data || [];

  if (portfolioCompanyId) {
    const { data: submissions } = await supabase.from('submissions').select('id').eq('portfolio_company_id', portfolioCompanyId);

    const submissionIds = new Set(submissions?.map((s) => s.id) || []);
    filteredData = filteredData.filter((f) => submissionIds.has(f.submission_id));
  }

  return {
    count: filteredData.length,
    totalBytes: filteredData.reduce((sum, f) => sum + (f.file_size || 0), 0),
  };
}
